import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  COMPLETE_REQUEST_WORKFLOWS_FAILURE_REASONS,
  SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS,
  WorkflowFormValidationError,
} from '@workflows/types';
import { XAlertService } from '@core/services';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import { WorkflowErrorsService } from './workflow-errors.service';
import { WorkflowDataService } from './workflow-data.service';

type WorkflowHttpAlertStatus = 'error' | 'warning';
type HttpErrorContext = 'sync' | 'fileUpload' | 'complete';

interface WorkflowHttpAlertConfig {
  titleKey: string;
  titleDefault: string;
  messageKey?: string;
  messageDefault?: string;
  status?: WorkflowHttpAlertStatus;
  autoClose?: number | boolean;
}

const NO_CONNECTION_ALERT: WorkflowHttpAlertConfig = {
  titleKey: 'workflows.errors.common.no_connection_title',
  titleDefault: 'Нет подключения к интернету',
  messageKey: 'workflows.errors.common.no_connection_message',
  messageDefault:
    'Данные будут отправлены автоматически после восстановления связи.',
  status: 'warning',
  autoClose: 6000,
};

const SYNC_SPECIFIC_ALERTS: Record<string, WorkflowHttpAlertConfig> = {
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.alreadyCompleted]: {
    titleKey: 'workflows.errors.sync.already_completed_title',
    titleDefault: 'Форма уже завершена',
    messageKey: 'workflows.errors.sync.already_completed_message',
    messageDefault: 'Эта форма уже была завершена ранее.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.logisticRequestNotFound]: {
    titleKey: 'workflows.errors.sync.request_not_found_title',
    titleDefault: 'Заявка не найдена',
    messageKey: 'workflows.errors.sync.request_not_found_message',
    messageDefault: 'Не удалось найти заявку для синхронизации формы.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.formNotFound]: {
    titleKey: 'workflows.errors.sync.form_not_found_title',
    titleDefault: 'Форма не найдена',
    messageKey: 'workflows.errors.sync.form_not_found_message',
    messageDefault: 'Форма регламента не найдена. Обновите страницу.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.requestWorkflowNotFound]: {
    titleKey: 'workflows.errors.sync.workflow_not_found_title',
    titleDefault: 'Регламент не найден',
    messageKey: 'workflows.errors.sync.workflow_not_found_message',
    messageDefault: 'Регламент не найден для этой заявки. Обновите страницу.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.badNextFormId]: {
    titleKey: 'workflows.errors.sync.bad_next_form_title',
    titleDefault: 'Некорректный следующий шаг',
    messageKey: 'workflows.errors.sync.bad_next_form_message',
    messageDefault:
      'Сервер отклонил переход к следующей форме. Обновите страницу.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.validationErrors]: {
    titleKey: 'workflows.errors.sync.validation_title',
    titleDefault: 'Обнаружены ошибки валидации',
    messageKey: 'workflows.errors.sync.validation_message',
    messageDefault:
      'Исправьте ошибки в форме и повторите отправку. Они подсвечены в полях.',
    status: 'warning',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.fileCategoryNotFound]: {
    titleKey: 'workflows.errors.sync.file_category_not_found_title',
    titleDefault: 'Категория файлов не найдена',
    messageKey: 'workflows.errors.sync.file_category_not_found_message',
    messageDefault: 'Для загружаемого файла не найдена категория файлов.',
  },
};

const COMPLETE_SPECIFIC_ALERTS: Record<string, WorkflowHttpAlertConfig> = {
  [COMPLETE_REQUEST_WORKFLOWS_FAILURE_REASONS.notAllRequestWorkflowsCompleted]:
    {
      titleKey: 'workflows.errors.complete.not_all_completed_title',
      titleDefault: 'Не все формы завершены',
      messageKey: 'workflows.errors.complete.not_all_completed_message',
      messageDefault:
        'Убедитесь, что все формы заполнены и отправлены перед завершением.',
    },
  [COMPLETE_REQUEST_WORKFLOWS_FAILURE_REASONS.alreadyCompleted]: {
    titleKey: 'workflows.errors.complete.already_completed_title',
    titleDefault: 'Регламенты уже завершены',
    messageKey: 'workflows.errors.complete.already_completed_message',
    messageDefault: 'Для этой заявки регламенты уже завершены.',
  },
  [COMPLETE_REQUEST_WORKFLOWS_FAILURE_REASONS.logisticRequestNotFound]: {
    titleKey: 'workflows.errors.complete.request_not_found_title',
    titleDefault: 'Заявка не найдена',
    messageKey: 'workflows.errors.complete.request_not_found_message',
    messageDefault: 'Не удалось найти заявку для завершения регламентов.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.validationErrors]: {
    titleKey: 'workflows.errors.complete.validation_title',
    titleDefault: 'Обнаружены ошибки валидации',
    messageKey: 'workflows.errors.complete.validation_message',
    messageDefault:
      'Исправьте ошибки в формах и повторите попытку завершения регламентов.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.workflowRequestMismatch]: {
    titleKey: 'workflows.errors.complete.workflow_request_mismatch_title',
    titleDefault: 'Конфликт регламента и заявки',
    messageKey: 'workflows.errors.complete.workflow_request_mismatch_message',
    messageDefault:
      'Регламент не соответствует заявке. Обновите страницу и повторите.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.requestWorkflowNotFound]: {
    titleKey: 'workflows.errors.complete.workflow_not_found_title',
    titleDefault: 'Регламент не найден',
    messageKey: 'workflows.errors.complete.workflow_not_found_message',
    messageDefault:
      'Один из регламентов не найден. Обновите страницу и повторите.',
  },
  [SYNC_WORKFLOW_RESPONSE_FAILURE_REASONS.duplicateSubmissions]: {
    titleKey: 'workflows.errors.complete.duplicate_submissions_title',
    titleDefault: 'Найдены дубли форм',
    messageKey: 'workflows.errors.complete.duplicate_submissions_message',
    messageDefault:
      'Обнаружены дубли отправок формы. Обновите страницу и повторите.',
  },
};

const DEFAULT_HTTP_ERROR_CONFIGS: Record<
  HttpErrorContext,
  Omit<WorkflowHttpAlertConfig, 'messageDefault'>
> = {
  sync: {
    titleKey: 'workflows.errors.sync.default_title',
    titleDefault: 'Ошибка синхронизации формы',
  },
  fileUpload: {
    titleKey: 'workflows.errors.sync.file_upload_title',
    titleDefault: 'Ошибка загрузки файла',
  },
  complete: {
    titleKey: 'workflows.errors.complete.default_title',
    titleDefault: 'Ошибка завершения регламентов',
  },
};

@Injectable({
  providedIn: 'root',
})
export class WorkflowHttpErrorsService {
  private readonly xAlertService = inject(XAlertService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly workflowErrorsService = inject(WorkflowErrorsService);
  private readonly workflowDataService = inject(WorkflowDataService);

  public showSyncHttpError(error: HttpErrorResponse): void {
    this.handleWorkflowHttpError(error, 'sync');
  }

  public showFileUploadHttpError(error: HttpErrorResponse): void {
    this.handleWorkflowHttpError(error, 'fileUpload');
  }

  public showCompleteHttpError(error: HttpErrorResponse): void {
    this.handleWorkflowHttpError(error, 'complete');
  }

  private handleWorkflowHttpError(
    error: HttpErrorResponse,
    context: HttpErrorContext,
  ): void {
    if (error.status === 0) {
      return this.showAlert(NO_CONNECTION_ALERT);
    }

    const failureReason = this.extractFailureReason(error);
    const mappedAlert = this.mapFailureReasonToAlert(failureReason, context);

    if (mappedAlert) {
      return this.showAlert(mappedAlert);
    }

    const fallback = DEFAULT_HTTP_ERROR_CONFIGS[context];

    this.showAlert({
      ...fallback,
      messageDefault: this.extractErrorMessage(error),
      status: 'error',
      autoClose: 10000,
    });
  }

  public showSyncValidationErrors(
    validationErrors?: WorkflowFormValidationError[],
  ): void {
    const detailedMessage = this.buildValidationErrorsMessage(validationErrors);

    if (detailedMessage) {
      this.showAlert({
        titleKey: 'workflows.errors.sync.validation_title',
        titleDefault: 'Обнаружены ошибки валидации',
        messageDefault: detailedMessage,
        status: 'warning',
        autoClose: 10000,
      });

      return;
    }

    this.showAlert({
      titleKey: 'workflows.errors.sync.validation_title',
      titleDefault: 'Обнаружены ошибки валидации',
      messageKey: 'workflows.errors.sync.validation_message',
      messageDefault:
        'Исправьте ошибки в форме и повторите отправку. Они подсвечены в полях.',
      status: 'warning',
      autoClose: 10000,
    });
  }

  public showCompleteValidationErrors(
    validationErrors?: WorkflowFormValidationError[],
  ): void {
    const detailedMessage = this.buildValidationErrorsMessage(validationErrors);

    if (detailedMessage) {
      this.showAlert({
        titleKey: 'workflows.errors.complete.validation_title',
        titleDefault: 'Обнаружены ошибки валидации',
        messageDefault: detailedMessage,
        status: 'error',
        autoClose: 10000,
      });

      return;
    }

    this.showAlert({
      titleKey: 'workflows.errors.complete.validation_title',
      titleDefault: 'Обнаружены ошибки валидации',
      messageKey: 'workflows.errors.complete.validation_message',
      messageDefault:
        'Исправьте ошибки в формах и повторите попытку завершения регламентов.',
      status: 'error',
      autoClose: 10000,
    });
  }

  private mapFailureReasonToAlert(
    failureReason: string | undefined,
    context: HttpErrorContext,
  ): WorkflowHttpAlertConfig | null {
    if (!failureReason) return null;

    switch (context) {
      case 'sync':
      case 'fileUpload':
        return SYNC_SPECIFIC_ALERTS[failureReason] ?? null;
      case 'complete':
        return COMPLETE_SPECIFIC_ALERTS[failureReason] ?? null;
      default:
        return null;
    }
  }

  private extractFailureReason(error: HttpErrorResponse): string | undefined {
    if (!error.error || typeof error.error !== 'object') {
      return undefined;
    }
    const failureReason = (error.error as { failureReason?: unknown })
      .failureReason;

    return typeof failureReason === 'string' ? failureReason : undefined;
  }

  private extractErrorMessage(error: HttpErrorResponse): string {
    const fallback = this.t(
      'workflows.errors.common.fallback_message',
      'Попробуйте снова или обратитесь в поддержку.',
    );

    if (!error.error) {
      return error.message || fallback;
    }

    if (typeof error.error === 'string') {
      return error.error;
    }

    if (typeof error.error === 'object') {
      const backendError = error.error as { message?: string; data?: string };

      return (
        backendError.data || backendError.message || error.message || fallback
      );
    }

    return error.message || fallback;
  }

  private showAlert(config: WorkflowHttpAlertConfig): void {
    const status = config.status ?? 'error';
    const autoClose = config.autoClose ?? 10000;
    const title = this.t(config.titleKey, config.titleDefault);
    const message =
      (config.messageKey && config.messageDefault
        ? this.t(config.messageKey, config.messageDefault)
        : config.messageDefault) || '';

    if (status === 'warning') {
      this.xAlertService.warn(title, message, autoClose);

      return;
    }

    this.xAlertService.error(title, message, autoClose);
  }

  private t(key: string, fallback: string): string {
    const translated = String(this.outkeepTranslateService.instant(key));

    return translated && translated !== key ? translated : fallback;
  }

  private buildValidationErrorsMessage(
    validationErrors?: WorkflowFormValidationError[],
  ): string | null {
    if (!Array.isArray(validationErrors) || validationErrors.length === 0) {
      return null;
    }

    const messages: string[] = [];

    for (const fieldError of validationErrors) {
      const fieldLabel = this.getValidationFieldLabel(fieldError);

      for (const error of fieldError.errors || []) {
        const readableMessage =
          this.workflowErrorsService.getValidationErrorMessage(
            error.validationRule,
            'ruleValue' in error ? error.ruleValue : undefined,
          );

        messages.push(
          fieldLabel ? `${fieldLabel}: ${readableMessage}` : readableMessage,
        );

        if (messages.length >= 3) {
          return messages.join('; ');
        }
      }
    }

    return messages.length > 0 ? messages.join('; ') : null;
  }

  private getValidationFieldLabel(
    fieldError: WorkflowFormValidationError,
  ): string {
    const parsedField = this.workflowErrorsService.parseFieldUrl(
      fieldError.fieldUrl,
    );

    if (!parsedField) {
      return '';
    }

    const wfData = this.workflowDataService.$requestsWorkflowData();

    if (!wfData) {
      return '';
    }

    const nodeIds = new Set(
      wfData.requestsWorkflows
        .filter(rw => rw.workflowVersion.workflow.id === parsedField.workflowId)
        .flatMap(rw => rw.nodes)
        .filter(node => node.key === parsedField.nodeKey)
        .map(node => node.id),
    );
    const form = wfData.workflowNodesForms.find(
      formItem =>
        formItem.key === parsedField.formKey &&
        nodeIds.has(formItem.workflowNodeId),
    );

    if (!form) {
      return '';
    }

    const field = form.schema.groups
      .flatMap(group => group.fields)
      .find((schemaField: any) => schemaField.key === parsedField.fieldKey);

    const title = typeof field?.title === 'string' ? field.title.trim() : '';

    return title || '';
  }
}
